import React, { useState, useMemo, useCallback } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary } from 'react-error-boundary';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  Typography,
  CircularProgress,
  Button
} from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PushNotificationProvider from './components/notifications/PushNotificationProvider';
import createAppTheme from './theme';
import AppRoutes from './Routes';

const THEME_STORAGE_KEY = 'app_theme';

export const ThemeModeContext = React.createContext({
  themeMode: 'light',
  setThemeMode: () => {}
});

function getStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (_) {}
  return 'light';
}

// Настройка флагов будущих функций React Router
// Это уберет предупреждения о будущих изменениях в v7
const routerFutureConfig = {
  v7_startTransition: true,
  v7_relativeSplatPath: true
};

// Компонент для отображения ошибок
function ErrorFallback({ error, resetErrorBoundary }) {
  const showTechnical =
    process.env.NODE_ENV !== 'production' && error?.message;
  return (
    <Box sx={{ 
      p: 3, 
      textAlign: 'center',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Typography variant="h4" color="error" gutterBottom>
        Произошла ошибка
      </Typography>
      <Typography color="text.secondary" paragraph>
        {showTechnical
          ? error.message
          : 'Что-то пошло не так. Попробуйте обновить страницу или зайти позже.'}
      </Typography>
      <Button
        variant="contained"
        onClick={resetErrorBoundary}
        sx={{ mt: 2 }}
      >
        Попробовать снова
      </Button>
    </Box>
  );
}

// Компонент загрузки
function LoadingFallback() {
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '100vh'
    }}>
      <CircularProgress />
    </Box>
  );
}

function App() {
  const [themeMode, setThemeModeState] = useState(getStoredTheme);

  const setThemeMode = useCallback((mode) => {
    const next = mode === 'dark' ? 'dark' : 'light';
    setThemeModeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  const themeContextValue = useMemo(
    () => ({ themeMode, setThemeMode }),
    [themeMode, setThemeMode]
  );

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.href = '/';
      }}
    >
      <ThemeProvider theme={theme}>
        <ThemeModeContext.Provider value={themeContextValue}>
          <CssBaseline />
          <Router future={routerFutureConfig}>
          <AuthProvider>
            <NotificationProvider>
              <PushNotificationProvider>
                <React.Suspense fallback={<LoadingFallback />}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh'
                  }}>
                    <AppRoutes />
                  </Box>
                </React.Suspense>
              </PushNotificationProvider>
            </NotificationProvider>
          </AuthProvider>
        </Router>
        </ThemeModeContext.Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 